import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CheckBox, Inscription, Preloader, RoundDoneIcon, Text } from '@common-fe/common-fe';
import _ from 'lodash';

import { CustomFieldError } from '@/common/types';
import { Modal } from '@/components';
import AppButton from '@/components/controls/AppButton';
import { SuccessModal } from '@/components/elements';
import { StatusWrapper } from '@/modules/core/components';
import { useAllowedCountriesQuery, useCountriesQuery } from '@/modules/core/queries';
import { useVerificationStore } from '@/modules/user/components/Verification/stores/useVerification.store';

import InvestingVerificationModal from './InvestingVerificationModal/InvestingVerificationModal';
import { useEmployeeData } from './PersonalInfoForm/hooks';
import usePersonalInfoValidators from './PersonalInfoForm/hooks/usePersonalInfoValidators';
import { usePersonalInfoStore } from './PersonalInfoForm/stores';
import ChangeCitizenshipBlock from './ChangeCitizenshipBlock';
import NotAllowedCitizenship from './NotAllowedCitizenship';
import PersonalInfoForm from './PersonalInfoForm';
import { useSavePersonalInfoQuery } from './queries';

interface Props {
  visible: boolean;
  isShowChangeCitizenshipDisplayed?: boolean;
  onSetVisible: (value: boolean) => void;
  onRejectByCitizenship: () => void;
  onResetRejectByCitizenship: () => void;
  onSetVisibleSuccessModal: (value: boolean) => void;
}

const PHONE_NUMBER_NOT_CORRECT = 'Phone number format is incorrect';
const PersonalInfoModal: React.FC<Props> = ({
  onResetRejectByCitizenship,
  visible,
  onSetVisible,
  onRejectByCitizenship,
  onSetVisibleSuccessModal,
  isShowChangeCitizenshipDisplayed,
}) => {
  const [isShowChangeCitizenship, setIsShowChangeCitizenship] = useState(false);

  useEffect(() => {
    if (isShowChangeCitizenshipDisplayed && !isShowChangeCitizenship) {
      setIsShowChangeCitizenship(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowChangeCitizenshipDisplayed]);

  const [successVisible, setSuccessVisible] = useState(false);
  const [customFieldErorrs, setCustomFieldErrors] = useState<CustomFieldError>({});
  const [notAllowedCitizenshipMode, setNotAllowedCitizenshipMode] = useState(false);
  const [isVerificationModalVisible, setIsVerificationModalVisible] = useState(false);
  const verificationChannels = useVerificationStore((state) => state.state.verificationChannels);
  const isVerificationNeeded = useMemo(
    () => verificationChannels && verificationChannels.length > 0,
    [verificationChannels]
  );
  const { allowedCountriesMap } = useAllowedCountriesQuery();
  const { rawData: rawCountries } = useCountriesQuery();
  const { t } = useTranslation();
  const { citizenShipId } = useEmployeeData();
  const { handleSave, isLoading } = useSavePersonalInfoQuery();

  const currentState = usePersonalInfoStore((state) => state.state);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isErrorShowed, setIsErrorShowed] = useState(false);
  const { validate } = usePersonalInfoValidators();

  const submit = useCallback(async () => {
    await handleSave(currentState);
    onSetVisible(false);

    if (isVerificationNeeded) {
      setIsVerificationModalVisible(true);
    } else {
      setSuccessVisible(true);
      onSetVisibleSuccessModal(true);
    }
  }, [
    isVerificationNeeded,
    handleSave,
    onSetVisible,
    setIsVerificationModalVisible,
    setSuccessVisible,
    onSetVisibleSuccessModal,
    currentState,
  ]);

  const foundCountry = useMemo(
    () =>
      rawCountries?.find((item) => {
        const currentId = `${item.id}`;

        return currentId === currentState.citizenship || currentId === citizenShipId;
      }),
    [citizenShipId, currentState.citizenship, rawCountries]
  );
  const isAllowed = useMemo(() => {
    if (foundCountry) {
      return (allowedCountriesMap && allowedCountriesMap[foundCountry?.alpha2 || '']) || false;
    }
    return null;
  }, [allowedCountriesMap, foundCountry]);

  const handleSubmit = useCallback(async () => {
    setIsShowChangeCitizenship(false);
    onResetRejectByCitizenship();
    setIsErrorShowed(true);
    if (isAccepted) {
      try {
        await validate(currentState);

        if (foundCountry) {
          if (isAllowed) {
            await submit();
          } else {
            await handleSave({
              ...currentState,
              isReject: true,
            });
            onRejectByCitizenship();
            onSetVisible(false);
            setIsShowChangeCitizenship(true);
            setNotAllowedCitizenshipMode(true);
          }
        }
      } catch (e) {
        const messages = _.get(
          e,
          'response.data.elevate_error_meta_info.invalid_fields',
          []
        ) as unknown as object[];
        messages.forEach((message) => {
          const currentMessage = _.get(message, 'elevate_error_message', '') as string;

          if (currentMessage.includes(PHONE_NUMBER_NOT_CORRECT)) {
            setCustomFieldErrors({
              phone: {
                value: currentState.phone || '',
                message: t('Phone number is not valid'),
              },
            });
          }
        });
      }
    }
  }, [
    onResetRejectByCitizenship,
    isAccepted,
    validate,
    currentState,
    foundCountry,
    isAllowed,
    submit,
    handleSave,
    onRejectByCitizenship,
    onSetVisible,
    t,
  ]);

  const handleCloseSuccess = useCallback(() => {
    setSuccessVisible(false);
    onSetVisibleSuccessModal(false);
    onSetVisible(false);
    // onSubmit();
  }, [onSetVisible, onSetVisibleSuccessModal]);

  const handleOpenSuccess = useCallback(() => {
    setSuccessVisible(true);
    onSetVisibleSuccessModal(true);
  }, [setSuccessVisible, onSetVisibleSuccessModal]);

  const handeCloseNotAllowedCitizenship = useCallback(() => {
    setNotAllowedCitizenshipMode(false);
    setIsShowChangeCitizenship(true);
  }, []);

  const handleOpen = useCallback(() => {
    onSetVisible(true);
    setIsErrorShowed(false);
  }, [onSetVisible]);

  return (
    <>
      {isShowChangeCitizenship && <ChangeCitizenshipBlock onOpen={handleOpen} />}

      {isVerificationNeeded && isVerificationModalVisible && (
        <InvestingVerificationModal
          handleOpenLetsGetStartedModal={handleOpen}
          onSetVisible={setIsVerificationModalVisible}
          onSetSuccessModal={handleOpenSuccess}
        />
      )}

      <SuccessModal
        icon={<RoundDoneIcon />}
        header={t('You’re all set!')}
        helptext={t("Now you're eligible to start investing!")}
        visible={successVisible}
        onSetVisible={handleCloseSuccess}
        buttonText={t('Got It!')}
        buttonStyle={{ width: '200px' }}
        testId="go-success_modal_wrapper"
      />
      <Box>
        <NotAllowedCitizenship
          visible={notAllowedCitizenshipMode}
          onClose={handeCloseNotAllowedCitizenship}
        />
        <Modal visible={successVisible ? false : visible} onSetVisible={onSetVisible} testId="personal-info">
          <Text
            weight="bold"
            textAlign="center"
            size="24px"
            // margin={{ top: 'spacing32', horizontal: 'spacing48' }}
          >
            {t('Lets Get Started')}
          </Text>
          <Box
            direction="column"
            background="module"
            elevation="default"
            pad="spacing24"
            round="moduleRound"
            margin={{ top: 'spacing24' }}
          >
            <PersonalInfoForm customFieldErorrs={customFieldErorrs} isErrorShowed={isErrorShowed} />
            <Box margin={{ top: 's' }}>
              <StatusWrapper status="pending">
                <Box background="canvas" elevation="default" pad="s" round="moduleRound">
                  <Box
                    round="container1Round"
                    direction="row"
                    background="warningContainer"
                    border={{ color: 'warningBorder' }}
                    pad={{ vertical: 'm', horizontal: 'spacing24' }}
                  >
                    <CheckBox
                      id="submitReguest"
                      className="checkbox-item"
                      data-testId="personal-info_accepted"
                      checked={isAccepted}
                      name={t('Enable notifications')}
                      onChange={() => setIsAccepted(!isAccepted)}
                      // error={!isAccepted && isErrorShowed}
                      label={
                        <Inscription size="medium" color="textBody" lineHeight="22px">
                          {t('I certify that the information submitted is accurate and complete')}
                        </Inscription>
                      }
                    />
                  </Box>
                </Box>
              </StatusWrapper>
            </Box>
          </Box>
          <Box direction="column">
            {isErrorShowed && !isAccepted && (
              <Box direction="column" align="end" margin={{ bottom: 'spacing24' }} fill>
                <Text color="danger">{t('Confirm that your data is correct to continue')}</Text>
              </Box>
            )}

            <Box direction="row" justify="end">
              <Box margin={{ right: 's' }}>
                <AppButton
                  testId="personal-info_cancel"
                  onClick={() => onSetVisible(false)}
                  buttonType="secondary"
                  width="control"
                >
                  Cancel
                </AppButton>
              </Box>
              <AppButton
                testId="personal-info_next"
                onClick={handleSubmit}
                disabled={isLoading}
                width="control"
              >
                {isLoading ? <Preloader color="white" /> : 'Next'}
              </AppButton>
            </Box>
          </Box>
        </Modal>
      </Box>
    </>
  );
};
export default PersonalInfoModal;
